import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import useAssignmentsUserCount, {
	ResolveAssignmentUsers
} from 'LearningManager/hooks/useAssignmentsUserCount';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import UserCountError from 'LearningManager/components/UserCountError';
import styles from './CourseResetModal.module.scss';
import { pluralLabel } from 'LearningManager/helpers/role';

export interface CourseResetAssignmentOption {
	id: number;
	label: string;
}

interface CourseResetModalProps {
	visible: boolean;
	options: CourseResetAssignmentOption[];
	resolveAssignmentUsers: ResolveAssignmentUsers;
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const CourseResetModal: React.FC<CourseResetModalProps> = ({
	visible,
	options,
	resolveAssignmentUsers,
	onCancel,
	onConfirm
}) => {
	const { t } = useTranslation();

	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const hasSelection = selectedIds.length > 0;

	const {
		count: userCount,
		failedCount,
		retry
	} = useAssignmentsUserCount(
		selectedIds,
		resolveAssignmentUsers,
		visible
	);

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title={t('learning.courseAssignments.courseResetModal.title')}
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						{t('learning.courseAssignments.courseResetModal.cancel')}
					</LMSButton>

					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						{t('learning.courseAssignments.courseResetModal.reset')}
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					{t(
						'learning.courseAssignments.courseResetModal.selectAssignments'
					)}
				</p>

				<div className={styles.field}>
					<div className={styles.field__label}>
						{t('learning.courseAssignments.courseResetModal.assignment')}
					</div>

					<MultiSelectDropdown
						options={options}
						placeholder={t(
							'learning.courseAssignments.courseResetModal.selectAssignment'
						)}
						defaultSelectedIds={[]}
						onChange={setSelectedIds}
						allLabel={t(
							'learning.courseAssignments.courseResetModal.allAssignments'
						)}
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{hasSelection && (
					<div className={styles.summary}>
						{failedCount > 0 && (
							<UserCountError
								failedCount={failedCount}
								noun={t(
									'learning.courseAssignments.courseResetModal.user'
								)}
								onRetry={retry}
							/>
						)}

						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.courseProgress',
								{
									userCount:
										userCount === undefined
											? ''
											: t(
												pluralLabel('learning.courseAssignments.courseResetModal.users', userCount),
												{ count: userCount }
											)
								}
							)}

							{userCount === undefined && (
								<Icon
									type="loading"
									aria-label={t(
										'learning.courseAssignments.courseResetModal.countingUsers'
									)}
								/>
							)}
						</p>

						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.progressRemoved'
							)}
						</p>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.startAgain'
							)}
						</p>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.cannotBeUndone'
							)}
						</p>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseResetModal;